import { createContext, useContext, ReactNode, useMemo, useCallback } from 'react';
import {
  useClients,
  useTransfers,
  useItems,
  useSales,
  useSettings,
  useTheme,
  useVendorPayments,
  useClientPayments,
} from '@/store/useStore';
import { Client, Transfer, Item, Sale, Settings, VendorPayment, ClientPayment } from '@/types';

interface AppContextType {
  // Loading
  isLoading: boolean;
  // Theme
  isDark: boolean;
  toggleTheme: () => void;
  // Clients
  clients: Client[];
  addClient: (c: Client) => void;
  updateClient: (c: Client) => void;
  deleteClient: (id: string) => void;
  setClients: (c: Client[]) => void;
  // Transfers
  transfers: Transfer[];
  addTransfer: (t: Transfer) => void;
  updateTransfer: (t: Transfer) => void;
  deleteTransfer: (id: string) => void;
  setTransfers: (t: Transfer[]) => void;
  // Items
  items: Item[];
  addItem: (i: Item) => void;
  updateItem: (i: Item) => void;
  deleteItem: (id: string) => void;
  setItems: (i: Item[]) => void;
  // Sales
  sales: Sale[];
  addSale: (s: Sale) => void;
  updateSale: (s: Sale, oldSale?: Sale) => void;
  deleteSale: (id: string) => void;
  setSales: (s: Sale[]) => void;
  // Settings
  settings: Settings;
  updateSettings: (s: Settings) => void;
  // Vendor Payments
  vendorPayments: VendorPayment[];
  addVendorPayment: (p: VendorPayment) => void;
  deleteVendorPayment: (id: string) => void;
  setVendorPayments: (p: VendorPayment[]) => void;
  // Client Payments
  clientPayments: ClientPayment[];
  addClientPayment: (p: ClientPayment) => void;
  deleteClientPayment: (id: string) => void;
  setClientPayments: (p: ClientPayment[]) => void;
}

const AppContext = createContext<AppContextType | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const { isDark, toggleTheme } = useTheme();
  const { clients, loading: l1, addClient, updateClient, deleteClient, setClients } = useClients();
  const { transfers, loading: l2, addTransfer, updateTransfer, deleteTransfer, setTransfers } = useTransfers();
  const { items, loading: l3, addItem, updateItem, deleteItem, setItems, setItemsDispatch } = useItems();
  const { sales, loading: l4, addSale: addSaleRaw, updateSale: updateSaleRaw, deleteSale: deleteSaleRaw, setSales } = useSales();
  const { settings, updateSettings } = useSettings();
  const { vendorPayments, loading: l5, addVendorPayment, deleteVendorPayment, setVendorPayments } = useVendorPayments();
  const { clientPayments, loading: l6, addClientPayment, deleteClientPayment, setClientPayments } = useClientPayments();

  const isLoading = useMemo(() => l1 || l2 || l3 || l4 || l5 || l6, [l1, l2, l3, l4, l5, l6]);

  // Wrap sales mutations to automatically adjust item stock
  const addSale = useCallback((s: Sale) => addSaleRaw(s, setItemsDispatch), [addSaleRaw, setItemsDispatch]);
  const updateSale = useCallback((s: Sale, oldSale?: Sale) => updateSaleRaw(s, oldSale, setItemsDispatch), [updateSaleRaw, setItemsDispatch]);
  const deleteSale = useCallback((id: string) => deleteSaleRaw(id, setItemsDispatch), [deleteSaleRaw, setItemsDispatch]);

  return (
    <AppContext.Provider
      value={{
        isLoading,
        isDark,
        toggleTheme,
        clients,
        addClient,
        updateClient,
        deleteClient,
        setClients,
        transfers,
        addTransfer,
        updateTransfer,
        deleteTransfer,
        setTransfers,
        items,
        addItem,
        updateItem,
        deleteItem,
        setItems,
        sales,
        addSale,
        updateSale,
        deleteSale,
        setSales,
        settings,
        updateSettings,
        vendorPayments,
        addVendorPayment,
        deleteVendorPayment,
        setVendorPayments,
        clientPayments,
        addClientPayment,
        deleteClientPayment,
        setClientPayments,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
